import { FC, useState } from "react";
import { Box, IconButton, Slider } from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import { playerFooterControlPadding } from "./playerFooterControlPadding";

interface VolumeControlProps {
  volume: number;
  muted: boolean;
  onToggleMute: () => void;
  onSetVolume: (volume: number) => void;
}

/**
 * Port of the reference MuteButton: the 50px slider sits to the LEFT of the
 * mute button, appears on hover/focus and stays while a drag is in
 * progress; it is never rendered while muted. No width animation or
 * overflow clipping — that used to cut the thumb in half at volume 1.
 */
export const VolumeControl: FC<VolumeControlProps> = ({
  volume,
  muted,
  onToggleMute,
  onSetVolume,
}) => {
  const [open, setOpen] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [mouseOver, setMouseOver] = useState(false);
  const displaySlider = open && !muted;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        pl: "10px",
      }}
      onMouseEnter={() => {
        setMouseOver(true);
        setOpen(true);
      }}
      onMouseLeave={() => {
        setMouseOver(false);
        if (!seeking) setOpen(false);
      }}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {displaySlider && (
        <Slider
          value={volume}
          min={0}
          max={1}
          step={0.01}
          onChange={(_event, value) => {
            setSeeking(true);
            onSetVolume(value as number);
          }}
          onChangeCommitted={() => {
            setSeeking(false);
            if (!mouseOver) setOpen(false);
          }}
          sx={{
            width: 50,
            mr: "10px",
            "@media (max-width: 450px)": { display: "none" },
            "& .MuiSlider-track": { backgroundColor: "#fff", border: 0 },
            "& .MuiSlider-rail": { backgroundColor: "#fff" },
            "& .MuiSlider-thumb": {
              backgroundColor: "#fff",
              "&:hover": {
                boxShadow: "0px 0px 0px 9px rgba(255, 255, 255, 0.16)",
              },
            },
          }}
        />
      )}
      <IconButton
        size="large"
        onClick={onToggleMute}
        sx={(theme) => ({
          color: "#fff",
          ...playerFooterControlPadding(theme),
        })}
      >
        {muted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
      </IconButton>
    </Box>
  );
};
